/**
 * @file src/store/index.js
 * @description Vuex store for the Event/Home component tree.
 *
 * Provides all getters, actions, and mutations consumed by the ported
 * marathon-photos-website Event components.
 *
 * State shape mirrors what the original application provided.  Getters that
 * return callable functions (e.g. isInCart) are implemented as method-style
 * getters per the Vuex docs.
 */
import Vue from 'vue';
import Vuex from 'vuex';
import { optimizePackPricing } from '@/utils/pricing';

Vue.use(Vuex);

const CART_STORAGE_KEY = 'commercial_cart'

const CART_MUTATIONS = new Set([
  'ADD_ITEM',
  'ADD_SINGLE_ITEM',
  'ADD_UPSELL',
  'CHECKOUT_REMOVE_ITEM',
  'CHECKOUT_UPDATE_QUANTITY',
  'CHECKOUT_EMPTY_CART',
  'CHECKOUT_SET_CART',
  'SET_IS_CHARITY',
])

/**
 * Vuex plugin that persists the sidebar cart to sessionStorage.
 * On every cart-related mutation the full cart array is saved;
 * on store init the saved cart is rehydrated and synced to checkoutCart.
 */
const CHARITY_STORAGE_KEY = 'commercial_is_charity'

function cartPersistencePlugin(store) {
  try {
    const saved = sessionStorage.getItem(CART_STORAGE_KEY)
    if (saved) {
      const cart = JSON.parse(saved)
      if (Array.isArray(cart) && cart.length > 0) {
        store.state.cart = cart
        syncCartToCheckout(store.state)
      }
    }
    const savedCharity = sessionStorage.getItem(CHARITY_STORAGE_KEY)
    if (savedCharity !== null) {
      store.state.isCharity = JSON.parse(savedCharity)
    }
  } catch {
    // storage unavailable or corrupt — start with empty cart
  }

  store.subscribe((mutation) => {
    if (CART_MUTATIONS.has(mutation.type)) {
      try {
        sessionStorage.setItem(
          CART_STORAGE_KEY,
          JSON.stringify(store.state.cart)
        )
        if (mutation.type === 'SET_IS_CHARITY') {
          sessionStorage.setItem(
            CHARITY_STORAGE_KEY,
            JSON.stringify(store.state.isCharity)
          )
        }
      } catch {
        // quota exceeded or storage unavailable — silently skip
      }
    }
  })
}

/**
 * Human-readable type names for product codes
 */
const PRODUCT_TYPE_NAMES = {
  dl_20x30: 'Digital Download',
  dl_cp20x30: 'Colour Pop',
  dl_rt20x30: 'Race Time Overlay',
  dl_eventcd: 'Event CD',
  dl_digsuperpack: 'Digital Super Pack',
  dl_megapack: 'Mega Pack',
  '$movie': 'Race Video',
}

/**
 * Derive a human-readable type name from a product identifier
 * @param {string} product - Product code (e.g. 'dl_20x30')
 * @returns {string} Human-readable type name
 */
function getTypeName(product) {
  if (!product) return ''
  for (const [key, name] of Object.entries(PRODUCT_TYPE_NAMES)) {
    if (product.indexOf(key) !== -1) return name
  }
  return product
}

/**
 * Convert a sidebar cart item to checkout cart item shape
 * @param {Object} sidebarItem - Item from state.cart
 * @returns {Object} Item shaped for state.checkoutCart
 */
function mapSidebarToCheckout(sidebarItem) {
  const id = sidebarItem.code ||
    `${sidebarItem.product}-${sidebarItem.bibNumber}-${sidebarItem.eventid}`
  const name = sidebarItem.eventName && sidebarItem.bibNumber
    ? `${sidebarItem.eventName} - Bib #${sidebarItem.bibNumber}`
    : sidebarItem.paramName || ''

  return {
    id,
    type: sidebarItem.product,
    name,
    eventId: sidebarItem.eventid,
    eventName: sidebarItem.eventName,
    paramName: sidebarItem.paramName,
    eventDate: sidebarItem.eventDate || '',
    typeName: getTypeName(sidebarItem.product),
    price: sidebarItem.price,
    commercialPrice: sidebarItem.commercialPrice,
    charityPrice: sidebarItem.charityPrice,
    currency: sidebarItem.currency,
    quantity: parseInt(sidebarItem.quantity) || 1,
    // Preserve sidebar-specific fields for API
    bibNumber: sidebarItem.bibNumber,
    subevent: sidebarItem.subevent,
    prefix: sidebarItem.prefix,
    code: sidebarItem.code,
    cropped: sidebarItem.cropped,
  }
}

/**
 * Rebuild checkoutCart from sidebar cart
 * @param {Object} state - Vuex state
 */
function syncCartToCheckout(state) {
  state.checkoutCart = state.cart
    .filter(i => parseInt(i.quantity) > 0)
    .map(mapSidebarToCheckout)
}

/**
 * Sync a checkout cart change back to the sidebar cart
 * @param {Object} state - Vuex state
 * @param {string} id - Checkout item id
 * @param {string} type - Checkout item type (product)
 */
function removeFromSidebarCart(state, id, type) {
  const idx = state.cart.findIndex(
    c => (c.code === id || `${c.product}-${c.bibNumber}-${c.eventid}` === id) && c.product === type
  )
  if (idx !== -1) {
    state.cart.splice(idx, 1)
  }
}

/**
 * Update quantity in sidebar cart to match checkout cart change
 * @param {Object} state - Vuex state
 * @param {string} id - Checkout item id
 * @param {string} type - Checkout item type (product)
 * @param {number} quantity - New quantity
 */
function updateSidebarCartQuantity(state, id, type, quantity) {
  const item = state.cart.find(
    c => (c.code === id || `${c.product}-${c.bibNumber}-${c.eventid}` === id) && c.product === type
  )
  if (item) {
    Vue.set(item, 'quantity', quantity)
  }
}

/**
 * Normalize a price value that may be a number, string, or object
 * @param {number|string|Object} price - Price value from API
 * @returns {number} Normalized numeric price
 */
function normalizePrice(price) {
  if (price !== null && typeof price === 'object') {
    return price.parsedValue ?? parseFloat(price.source) ?? 0
  }
  return typeof price === 'number' ? price : parseFloat(price) || 0
}

/**
 * Normalize dual pricing fields from the commercial_details API.
 * If the item has commercial_price/charity_price, stores both and resolves
 * item.price based on the isCharity flag. Falls back to single-price normalization.
 * @param {Object} item - Pricing item from API
 * @param {boolean} isCharity - Whether charity pricing is active
 */
function normalizeDualPrice(item, isCharity) {
  if (item.commercial_price !== undefined) {
    Vue.set(item, 'commercialPrice', normalizePrice(item.commercial_price))
    Vue.set(item, 'charityPrice', normalizePrice(item.charity_price))
    Vue.set(item, 'price', isCharity ? item.charityPrice : item.commercialPrice)
  } else {
    Vue.set(item, 'price', normalizePrice(item.price))
    Vue.set(item, 'commercialPrice', item.price)
    Vue.set(item, 'charityPrice', item.price)
  }
}

export default new Vuex.Store({
  plugins: [cartPersistencePlugin],
  state: {
    event: {},
    athlete: {},
    searchFailed: {},
    linkedOrder: {},
    cartError: false,
    eventType: '',
    shareToken: '',
    authDetails: {},
    eventAuth: [],
    currency: { USD: 'USD $', EUR: '€', GBP: '£', AUD: 'AUD $', NZD: 'NZD $', CAD: 'CAD $', CHF: 'Fr.', JPY: '¥', CNY: '¥' },
    eventProducts: [],
    offers: [],
    cart: [],
    relatedEvents: [],
    linkPrefix: '',
    linkBib: '',
    scrolledState: false,
    acceptedNotification: false,
    allowSelfie: false,
    doAuth: false,
    isCharity: false,
    eventSurname: false,
    countryList: {
      "AD": "Andorra",
      "AE": "United Arab Emirates",
      "AF": "Afghanistan",
      "AG": "Antigua and Barbuda",
      "AI": "Anguilla",
      "AL": "Albania",
      "AM": "Armenia",
      "AO": "Angola",
      "AQ": "Antarctica",
      "AR": "Argentina",
      "AS": "American Samoa",
      "AT": "Austria",
      "AU": "Australia",
      "AW": "Aruba",
      "AX": "Aland Islands",
      "AZ": "Azerbaijan",
      "BA": "Bosnia and Herzegovina",
      "BB": "Barbados",
      "BD": "Bangladesh",
      "BE": "Belgium",
      "BF": "Burkina Faso",
      "BG": "Bulgaria",
      "BH": "Bahrain",
      "BI": "Burundi",
      "BJ": "Benin",
      "BM": "Bermuda",
      "BN": "Brunei Darussalam",
      "BO": "Bolivia (Plurinational State of)",
      "BR": "Brazil",
      "BS": "Bahamas",
      "BT": "Bhutan",
      "BV": "Bouvet Island",
      "BW": "Botswana",
      "BY": "Belarus",
      "BZ": "Belize",
      "CA": "Canada",
      "CC": "Cocos (Keeling) Islands",
      "CD": "Congo, Democratic Republic of the",
      "CF": "Central African Republic",
      "CG": "Congo",
      "CH": "Switzerland",
      "CI": "Côte d'Ivoire",
      "CK": "Cook Islands",
      "CL": "Chile",
      "CM": "Cameroon",
      "CN": "China",
      "CO": "Colombia",
      "CR": "Costa Rica",
      "CU": "Cuba",
      "CV": "Cabo Verde",
      "CX": "Christmas Island",
      "CY": "Cyprus",
      "CZ": "Czechia",
      "DE": "Germany",
      "DJ": "Djibouti",
      "DK": "Denmark",
      "DM": "Dominica",
      "DO": "Dominican Republic",
      "DZ": "Algeria",
      "EC": "Ecuador",
      "EE": "Estonia",
      "EG": "Egypt",
      "EH": "Western Sahara",
      "ER": "Eritrea",
      "ES": "Spain",
      "ET": "Ethiopia",
      "FI": "Finland",
      "FJ": "Fiji",
      "FK": "Falkland Islands (Malvinas)",
      "FM": "Micronesia (Federated States of)",
      "FO": "Faroe Islands",
      "FR": "France",
      "GA": "Gabon",
      //"GB": "United Kingdom",
      "GD": "Grenada",
      "GE": "Georgia",
      "GF": "French Guiana",
      "GH": "Ghana",
      "GI": "Gibraltar",
      "GL": "Greenland",
      "GM": "Gambia",
      "GN": "Guinea",
      "GP": "Guadeloupe",
      "GQ": "Equatorial Guinea",
      "GR": "Greece",
      "GS": "South Georgia and the South Sandwich Islands",
      "GT": "Guatemala",
      "GU": "Guam",
      "GW": "Guinea-Bissau",
      "GY": "Guyana",
      "HK": "Hong Kong",
      "HM": "Heard Island and McDonald Islands",
      "HN": "Honduras",
      "HR": "Croatia",
      "HT": "Haiti",
      "HU": "Hungary",
      "ID": "Indonesia",
      "IE": "Ireland",
      "IL": "Israel",
      "IN": "India",
      "IO": "British Indian Ocean Territory",
      "IQ": "Iraq",
      "IR": "Iran (Islamic Republic of)",
      "IS": "Iceland",
      "IT": "Italy",
      "JM": "Jamaica",
      "JO": "Jordan",
      "JP": "Japan",
      "KE": "Kenya",
      "KG": "Kyrgyzstan",
      "KH": "Cambodia",
      "KI": "Kiribati",
      "KM": "Comoros",
      "KN": "Saint Kitts and Nevis",
      "KP": "Korea (Democratic People's Republic of)",
      "KR": "Korea, Republic of",
      "KW": "Kuwait",
      "KY": "Cayman Islands",
      "KZ": "Kazakhstan",
      "LA": "Lao People's Democratic Republic",
      "LB": "Lebanon",
      "LC": "Saint Lucia",
      "LI": "Liechtenstein",
      "LK": "Sri Lanka",
      "LR": "Liberia",
      "LS": "Lesotho",
      "LT": "Lithuania",
      "LU": "Luxembourg",
      "LV": "Latvia",
      "LY": "Libya",
      "MA": "Morocco",
      "MC": "Monaco",
      "MD": "Moldova, Republic of",
      "ME": "Montenegro",
      "MG": "Madagascar",
      "MH": "Marshall Islands",
      "MK": "North Macedonia",
      "ML": "Mali",
      "MM": "Myanmar",
      "MN": "Mongolia",
      "MO": "Macao",
      "MP": "Northern Mariana Islands",
      "MQ": "Martinique",
      "MR": "Mauritania",
      "MS": "Montserrat",
      "MT": "Malta",
      "MU": "Mauritius",
      "MV": "Maldives",
      "MW": "Malawi",
      "MX": "Mexico",
      "MY": "Malaysia",
      "MZ": "Mozambique",
      "NA": "Namibia",
      "NC": "New Caledonia",
      "NE": "Niger",
      "NF": "Norfolk Island",
      "NG": "Nigeria",
      "NI": "Nicaragua",
      "NL": "Netherlands",
      "AN": "Netherlands Antilles",
      "NO": "Norway",
      "NP": "Nepal",
      "NR": "Nauru",
      "NU": "Niue",
      "NZ": "New Zealand",
      "OM": "Oman",
      "PA": "Panama",
      "PE": "Peru",
      "PF": "French Polynesia",
      "PG": "Papua New Guinea",
      "PH": "Philippines",
      "PK": "Pakistan",
      "PL": "Poland",
      "PM": "Saint Pierre and Miquelon",
      "PN": "Pitcairn",
      "PR": "Puerto Rico",
      "PS": "Palestine, State of",
      "PT": "Portugal",
      "PW": "Palau",
      "PY": "Paraguay",
      "QA": "Qatar",
      "RE": "Réunion",
      "RO": "Romania",
      "RS": "Serbia",
      "RU": "Russian Federation",
      "RW": "Rwanda",
      "SA": "Saudi Arabia",
      "SB": "Solomon Islands",
      "SC": "Seychelles",
      "SD": "Sudan",
      "SE": "Sweden",
      "SG": "Singapore",
      "SH": "Saint Helena, Ascension and Tristan da Cunha",
      "SI": "Slovenia",
      "SJ": "Svalbard and Jan Mayen",
      "SK": "Slovakia",
      "SL": "Sierra Leone",
      "SM": "San Marino",
      "SN": "Senegal",
      "SO": "Somalia",
      "SR": "Suriname",
      "ST": "Sao Tome and Principe",
      "SV": "El Salvador",
      "SY": "Syrian Arab Republic",
      "SZ": "Eswatini",
      "TC": "Turks and Caicos Islands",
      "TD": "Chad",
      "TF": "French Southern Territories",
      "TG": "Togo",
      "TH": "Thailand",
      "TJ": "Tajikistan",
      "TK": "Tokelau",
      "TL": "Timor-Leste",
      "TM": "Turkmenistan",
      "TN": "Tunisia",
      "TO": "Tonga",
      "TR": "Turkey",
      "TT": "Trinidad and Tobago",
      "TV": "Tuvalu",
      "TW": "Taiwan Region",
      "TZ": "Tanzania, United Republic of",
      "UA": "Ukraine",
      "UG": "Uganda",
      "GB": "United Kingdom",
      "UM": "United States Minor Outlying Islands",
      "US": "United States of America",
      "UY": "Uruguay",
      "UZ": "Uzbekistan",
      "VA": "Holy See",
      "VC": "Saint Vincent and the Grenadines",
      "VE": "Venezuela (Bolivarian Republic of)",
      "VG": "Virgin Islands (British)",
      "VI": "Virgin Islands (U.S.)",
      "VN": "Vietnam",
      "VU": "Vanuatu",
      "WF": "Wallis and Futuna",
      "WS": "Samoa",
      "YE": "Yemen",
      "YT": "Mayotte",
      "ZA": "South Africa",
      "ZM": "Zambia",
      "ZW": "Zimbabwe",
    },
    hasRT: false,
    // Checkout-specific state
    checkoutCart: [],
    orderDetails: { access_code: '', order_id: '' },
    countries: {},
    paymentOptions: [],
  },

  getters: {
    getEvent: (state) => state.event,
    getAthlete: (state) => state.athlete,
    getSearchFailed: (state) => state.searchFailed,
    getLinkedOrder: (state) => state.linkedOrder,
    getCartError: (state) => state.cartError,
    getEventType: (state) => state.eventType,
    getShareToken: (state) => state.shareToken,
    getAuthDetails: (state) => state.authDetails,
    getEventAuth: (state) => state.eventAuth,
    getCurrency: (state) => state.currency,
    getEventProducts: (state) => {
      const athlete = state.athlete
      let subevent = ''
      if (athlete && athlete.details && athlete.details.Subevent) {
        subevent = athlete.details.Subevent
      }
      const effectiveSubevent = subevent || state.event.defaultsubevent || ''
      return state.offers.map(offer => ({
        ...offer,
        items: (offer.items || []).filter(item =>
          item.price >= 0 && (
            item.subevent === effectiveSubevent ||
            effectiveSubevent === '' ||
            !item.subevent
          )
        ).map(item => ({
          ...item,
          subevent: item.subevent || effectiveSubevent
        }))
      }))
    },
    getCart: (state) => state.cart,
    getRelatedEvents: (state) => state.relatedEvents,
    getLinkPrefix: (state) => state.linkPrefix,
    getLinkBib: (state) => state.linkBib,
    getScrolledState: (state) => state.scrolledState,
    hasAcceptedNotification: (state) => state.acceptedNotification,
    getAllowSelfie: (state) => state.allowSelfie,
    isCharity: (state) => state.isCharity,
    hasRT: (state) => state.hasRT,
    countryList: (state) => (code) => {
      if (Array.isArray(state.countryList)) {
        const found = state.countryList.find((c) => c.code === code)
        return found ? found.name : ''
      }
      return state.countryList[code] || ''
    },
    canDoNotification: () => {
      return 'Notification' in window && 'serviceWorker' in navigator;
    },
    /**
     * Method-style getter – returns a function so components can call
     * this.$store.getters.isInCart(offer)
     */
    isInCart: (state) => (offer) => {
      if (!offer) return false;
      return state.cart.some(
        (item) =>
          item.product === offer.product &&
          item.bibNumber === offer.bibNumber &&
          item.eventid === offer.eventid,
      );
    },
    // Checkout getters
    cartItems: (state) => state.checkoutCart,
    cartItemCount: (state) => state.checkoutCart.reduce((total, item) => total + item.quantity, 0),
    cartTotal: (state) => state.checkoutCart.reduce((total, item) => total + (item.price * item.quantity), 0),
    isCartEmpty: (state) => state.checkoutCart.length === 0,
    checkoutCurrency: (state) => state.checkoutCart[0]?.currency || 'NZD',
    cartCurrency: (state) => state.cart.length > 0 ? state.cart[0].currency : null,
    orderDetails: (state) => state.orderDetails,
    countries: (state) => Object.keys(state.countries).length > 0 ? state.countries : state.countryList,
    paymentOptions: (state) => state.paymentOptions,
  },

  mutations: {
    RESET_SEARCH(state) {
      state.event = {}
      state.athlete = {}
      state.searchFailed = {}
      state.linkedOrder = {}
      state.eventType = ''
      state.offers = []
      state.relatedEvents = []
    },
    SET_EVENT(state, event) {
      state.event = event;
      // Populate offers from currency_pricing
      if (event && event.currency_pricing && event.currency_pricing.length > 0) {
        state.offers = event.currency_pricing;
        const defaultSubevent = event.defaultsubevent || event.event?.split('/').pop() || '';
        state.event.defaultsubevent = defaultSubevent;
        // Normalize prices (API may return objects like {source, parsedValue})
        state.offers.forEach(currency => {
          currency.items.forEach(item => {
            normalizeDualPrice(item, state.isCharity)
          })
        })
      }
    },
    SET_ATHLETE(state, athlete) {
      state.athlete = athlete;
      // Athlete-specific pricing overrides event pricing
      if (athlete && athlete.currency_pricing && athlete.currency_pricing.length > 0) {
        state.offers = athlete.currency_pricing;
        // Normalize prices (API may return objects like {source, parsedValue})
        state.offers.forEach(currency => {
          currency.items.forEach(item => {
            normalizeDualPrice(item, state.isCharity)
          })
        })
      }
    },
    SET_SEARCH_FAILED(state, payload) {
      state.searchFailed = payload;
    },
    SET_LINKED_ORDER(state, order) {
      state.linkedOrder = order;
    },
    SET_CART_ERROR(state, { carterror }) {
      state.cartError = carterror;
    },
    SET_EVENT_TYPE(state, { eventtype }) {
      state.eventType = eventtype;
    },
    SET_SHARE_TOKEN(state, payload) {
      state.shareToken = payload;
    },
    SET_AUTH_DETAILS(state, { authdetails }) {
      state.authDetails = authdetails;
    },
    SET_LINK_PREFIX(state, { linkPrefix }) {
      state.linkPrefix = linkPrefix;
    },
    SET_LINK_BIB(state, { linkBib }) {
      state.linkBib = linkBib;
    },
    SET_RELATED_EVENTS(state, events) {
      state.relatedEvents = events;
    },
    SET_ALLOW_SELFIE(state, val) {
      state.allowSelfie = val;
    },
    SET_DO_AUTH(state, val) {
      state.doAuth = val;
    },
    SET_EVENT_SURNAME(state, val) {
      state.eventSurname = val;
    },
    SET_IS_CHARITY(state, isCharity) {
      state.isCharity = isCharity
      state.offers.forEach(currency => {
        currency.items.forEach(item => {
          if (item.commercialPrice !== undefined) {
            Vue.set(item, 'price', isCharity ? item.charityPrice : item.commercialPrice)
          }
        })
      })
      state.cart.forEach(item => {
        if (item.commercialPrice !== undefined) {
          Vue.set(item, 'price', isCharity ? item.charityPrice : item.commercialPrice)
        }
      })
      syncCartToCheckout(state)
    },
    ADD_ITEM(state, item) {
      state.cart.push(item);
      syncCartToCheckout(state);
    },
    ADD_SINGLE_ITEM(state, item) {
      const idx = state.cart.findIndex(
        (c) => c.product === item.product && c.bibNumber === item.bibNumber && c.eventid === item.eventid,
      );
      if (item.quantity === 0 && idx !== -1) {
        state.cart.splice(idx, 1);
      } else if (idx !== -1) {
        Vue.set(state.cart, idx, item);
      } else if (item.quantity > 0) {
        state.cart.push(item);
      }
      syncCartToCheckout(state);
    },
    ADD_UPSELL(state, upsell) {
      const offer = upsell.product;
      const idx = state.cart.findIndex(
        (c) =>
          c.product === offer.product &&
          c.bibNumber === offer.bibNumber &&
          c.eventid === offer.eventid,
      );
      if (offer.quantity === 0 && idx !== -1) {
        state.cart.splice(idx, 1);
      } else if (idx !== -1) {
        Vue.set(state.cart, idx, offer);
      } else if (offer.quantity > 0) {
        state.cart.push(offer);
      }
      syncCartToCheckout(state);
    },
    SET_HAS_RT(state, val) {
      state.hasRT = val;
    },
    SET_ACCEPTED_NOTIFICATION(state, val) {
      state.acceptedNotification = val;
    },
    // Checkout mutations
    CHECKOUT_ADD_ITEM(state, item) {
      const existingIndex = state.checkoutCart.findIndex(
        (i) => i.id === item.id && i.type === item.type
      );
      if (existingIndex >= 0) {
        state.checkoutCart[existingIndex].quantity += item.quantity || 1;
      } else {
        state.checkoutCart.push({ ...item, quantity: item.quantity || 1 });
      }
    },
    CHECKOUT_REMOVE_ITEM(state, { id, type }) {
      state.checkoutCart = state.checkoutCart.filter(
        (item) => !(item.id === id && item.type === type)
      );
      removeFromSidebarCart(state, id, type);
    },
    CHECKOUT_UPDATE_QUANTITY(state, { id, type, quantity }) {
      const item = state.checkoutCart.find((i) => i.id === id && i.type === type);
      if (item) {
        item.quantity = quantity;
      }
      updateSidebarCartQuantity(state, id, type, quantity);
    },
    CHECKOUT_EMPTY_CART(state) {
      state.checkoutCart = [];
      state.cart = [];
    },
    CHECKOUT_SET_CART(state, items) {
      state.checkoutCart = items;
    },
    CHECKOUT_SAVE_ORDER(state, orderDetails) {
      state.orderDetails = orderDetails;
    },
    CHECKOUT_CLEAR_ORDER(state) {
      state.orderDetails = { access_code: '', order_id: '' };
    },
    SET_COUNTRIES(state, countries) {
      state.countries = countries;
    },
    SET_PAYMENT_OPTIONS(state, options) {
      state.paymentOptions = options;
    },
  },

  actions: {
    resetSearch({ commit }) {
      commit('RESET_SEARCH')
    },
    setEvent({ commit }, event) {
      commit('SET_EVENT', event);
      if (event && event.auth) {
        // Derive eventAuth from event data if available
      }
      if (event && event.has_race_time) {
        commit('SET_HAS_RT', true);
      }
      return event;
    },
    setAthlete({ commit }, athlete) {
      commit('SET_ATHLETE', athlete);
    },
    setSearchFailed({ commit }, payload) {
      commit('SET_SEARCH_FAILED', payload);
    },
    setLinkedOrder({ commit }, order) {
      commit('SET_LINKED_ORDER', order);
    },
    setCartError({ commit }, payload) {
      commit('SET_CART_ERROR', payload);
    },
    setEventType({ commit }, payload) {
      commit('SET_EVENT_TYPE', payload);
    },
    setShareToken({ commit }, payload) {
      commit('SET_SHARE_TOKEN', payload);
    },
    setAuthDetails({ commit }, payload) {
      commit('SET_AUTH_DETAILS', payload);
    },
    setLinkPrefix({ commit }, payload) {
      commit('SET_LINK_PREFIX', payload);
    },
    setLinkBib({ commit }, payload) {
      commit('SET_LINK_BIB', payload);
    },
    setRelatedEvents({ commit }, events) {
      commit('SET_RELATED_EVENTS', events);
    },
    setAllowSelfie({ commit }, val) {
      commit('SET_ALLOW_SELFIE', val);
    },
    setDoAuth({ commit }, val) {
      commit('SET_DO_AUTH', val);
    },
    setEventSurname({ commit }, val) {
      commit('SET_EVENT_SURNAME', val);
      return val;
    },
    setIsCharity({ commit, state }, isCharity) {
      commit('SET_IS_CHARITY', isCharity)
      if (state.offers.length > 0 && state.offers[0].items) {
        const event = state.event
        if (event.prelive && !event.live) {
          optimizePackPricing(state.offers[0].items, { trackMovie: true })
        } else if (event.live && state.athlete?.currency_pricing) {
          optimizePackPricing(state.offers[0].items)
        }
      }
    },
    addItem({ commit, state }, item) {
      const existing = state.cart.length > 0 ? state.cart[0].currency : null
      if (existing && item.currency !== existing) {
        commit('SET_CART_ERROR', { carterror: true })
        return
      }
      commit('SET_CART_ERROR', { carterror: false })
      commit('ADD_ITEM', item)
    },
    addSingleItem({ commit, state }, item) {
      const existing = state.cart.length > 0 ? state.cart[0].currency : null
      if (existing && item.currency !== existing) {
        commit('SET_CART_ERROR', { carterror: true })
        return
      }
      commit('SET_CART_ERROR', { carterror: false })
      commit('ADD_SINGLE_ITEM', item)
    },
    addUpsell({ commit, state }, upsell) {
      const existing = state.cart.length > 0 ? state.cart[0].currency : null
      if (existing && upsell.currency !== existing) {
        commit('SET_CART_ERROR', { carterror: true })
        return
      }
      commit('SET_CART_ERROR', { carterror: false })
      commit('ADD_UPSELL', upsell)
    },
    // Checkout actions
    checkoutAddItem({ commit }, item) {
      commit('CHECKOUT_ADD_ITEM', item);
    },
    checkoutRemoveItem({ commit }, payload) {
      commit('CHECKOUT_REMOVE_ITEM', payload);
    },
    checkoutUpdateQuantity({ commit }, payload) {
      commit('CHECKOUT_UPDATE_QUANTITY', payload);
    },
    checkoutEmptyCart({ commit }) {
      commit('CHECKOUT_EMPTY_CART');
    },
    checkoutSaveOrder({ commit }, orderDetails) {
      commit('CHECKOUT_SAVE_ORDER', orderDetails);
    },
    checkoutSyncCart({ commit }, items) {
      commit('CHECKOUT_SET_CART', items);
    },
    setCountries({ commit }, countries) {
      commit('SET_COUNTRIES', countries);
    },
    setPaymentOptions({ commit }, options) {
      commit('SET_PAYMENT_OPTIONS', options);
    },
  },
});
